import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { BFF_URL } from "../../../config";
import { useErrorNotification } from "../../hooks/useErrorNotification";

export const BASE_API_URL = `${BFF_URL}/api/v1`;

export class HttpError extends Error {
    constructor(public response: Response) {
        super(`HTTP ${response.status} ${response.statusText}`);
        this.name = "HttpError";
    }
}

export interface ErrorMeta extends Record<string, unknown> {
    suppressErrorToast?: boolean;
    errorMessage?: string;
    resourceName?: string;
}

// Module augmentation: narrows the `meta` type of useQuery/useMutation from
// the default `Record<string, unknown>` to our `ErrorMeta`. Purely for type
// safety and autocomplete at call sites — runtime behaviour is unaffected.
declare module "@tanstack/react-query" {
    interface Register {
        queryMeta: ErrorMeta;
        mutationMeta: ErrorMeta;
    }
}

export const useTaxManagerAppQueryClient = () => {
    const { showTextMessage, showFetchErrorMessage } = useErrorNotification();

    return useMemo(() => {
        const handleError = (error: unknown, meta: ErrorMeta | undefined) => {
            if (meta?.suppressErrorToast) return;
            // For HTTP errors prefer the message coming from the BFF response
            // body, falling back to meta.errorMessage only when the BFF does
            // not provide one.
            if (error instanceof HttpError) {
                showFetchErrorMessage(
                    error.response,
                    meta?.resourceName,
                    meta?.errorMessage,
                );
                return;
            }
            if (meta?.errorMessage || meta?.resourceName) {
                showTextMessage(meta.errorMessage, meta.resourceName);
            }
        };

        return new QueryClient({
            queryCache: new QueryCache({
                onError: (error, query) => handleError(error, query.meta),
            }),
            mutationCache: new MutationCache({
                onError: (error, _vars, _ctx, mutation) =>
                    handleError(error, mutation.meta),
            }),
            defaultOptions: {
                queries: {
                    refetchOnWindowFocus: false, // default: true
                },
            },
        });
    }, [showTextMessage, showFetchErrorMessage]);
};
